import { CheckCircle2, CircleAlert, X } from 'lucide-react'
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { cn } from '../../lib/cn'

interface ToastItem {
  id: number
  message: string
  tone: 'success' | 'error'
}

interface ToastContextValue {
  showToast(message: string, tone?: ToastItem['tone']): void
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const showToast = useCallback((message: string, tone: ToastItem['tone'] = 'success') => {
    const id = Date.now() + Math.random()
    setItems((current) => [...current, { id, message, tone }])
    window.setTimeout(() => setItems((current) => current.filter((item) => item.id !== id)), 4500)
  }, [])
  const value = useMemo(() => ({ showToast }), [showToast])
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed inset-x-4 top-4 z-[100] flex flex-col items-end gap-2 sm:left-auto sm:w-[380px]">
        {items.map((item) => (
          <div
            key={item.id}
            role="status"
            className={cn(
              'pointer-events-auto flex w-full items-start gap-3 rounded-2xl border bg-[var(--surface)] p-4 shadow-xl animate-in fade-in slide-in-from-top-2',
              item.tone === 'error'
                ? 'border-[var(--destructive-border)]'
                : 'border-[var(--success-border)]',
            )}
          >
            {item.tone === 'error' ? (
              <CircleAlert className="mt-0.5 size-5 shrink-0 text-[var(--destructive)]" />
            ) : (
              <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-[var(--success)]" />
            )}
            <p className="flex-1 text-sm font-medium leading-5">{item.message}</p>
            <button
              type="button"
              aria-label="Chiudi notifica"
              className="rounded-lg p-1 text-[var(--muted-foreground)] hover:bg-[var(--muted)]"
              onClick={() => setItems((current) => current.filter((entry) => entry.id !== item.id))}
            >
              <X className="size-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast deve essere usato dentro ToastProvider')
  return context
}
